/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { walletApi, fmtDate } from "@/lib/wallet";

/**
 * Bell icon + dropdown for the customer header. Polls every 30 s.
 */
export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await walletApi.get("/customer/notifications?limit=10");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (err) {
      console.error("Could not load notifications", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  async function markAll() {
    try { await walletApi.post("/customer/notifications/read-all"); load(); }
    catch (err) { console.error("mark-all-read failed", err); }
  }
  async function markOne(id) {
    try { await walletApi.post(`/customer/notifications/${id}/read`); load(); }
    catch (err) { console.error("mark-one-read failed", err); }
  }

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} data-testid="notif-bell"
        className="relative rounded-full p-2 hover:bg-brand-bgSoft" aria-label="Notifications">
        <Bell className="h-5 w-5 text-brand-ink/80" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-brand-green text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="notif-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-2xl bg-white border border-brand-line shadow-xl overflow-hidden z-30" data-testid="notif-dropdown">
          <div className="px-4 py-3 border-b border-brand-line flex items-center justify-between">
            <p className="font-semibold text-sm">Notifications</p>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notif-mark-all"
                className="text-xs font-semibold text-brand-greenDark hover:text-brand-greenHover">Mark all read</button>
            )}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <li className="py-8 text-center text-sm text-brand-muted">You're all caught up</li>
            )}
            {items.map((n) => (
              <li key={n.id}>
                {n.link ? (
                  <Link to={n.link} onClick={() => markOne(n.id)} data-testid={`notif-item-${n.id}`}
                    className={`block px-4 py-3 hover:bg-brand-bgSoft border-b border-brand-line ${!n.read_at ? "bg-brand-greenSoft/30" : ""}`}>
                    <p className="text-sm font-semibold">{n.title}</p>
                    {n.body && <p className="text-xs text-brand-muted mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] text-brand-muted mt-1">{fmtDate(n.created_at)}</p>
                  </Link>
                ) : (
                  <button onClick={() => markOne(n.id)}
                    className={`w-full text-left block px-4 py-3 hover:bg-brand-bgSoft border-b border-brand-line ${!n.read_at ? "bg-brand-greenSoft/30" : ""}`}>
                    <p className="text-sm font-semibold">{n.title}</p>
                    {n.body && <p className="text-xs text-brand-muted mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] text-brand-muted mt-1">{fmtDate(n.created_at)}</p>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
